import { Component, inject, signal, effect, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SessionService } from '../../core/session.service';
import { WizardConfigService, WizardConfigResponse, QueueConfig } from '../../core/services/wizard-config.service';
import { ToastService } from '../../core/services/toast.service';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile-ia',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './profile-ia.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./profile-ia.component.scss']
})
export class ProfileIaComponent implements OnInit {
  private session = inject(SessionService);
  private wizardConfigService = inject(WizardConfigService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  loading = signal(true);
  saving = signal(false);
  configs = signal<WizardConfigResponse>([]);
  selectedQueue = signal<QueueConfig | null>(null);
  hasChanges = signal<boolean>(false);
  showConfirmModal = signal<boolean>(false);
  
  private originalConfig: any = null;
  
  days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  /** Le jour reste stocké en français (contrat de données avec le backend) ; seul le libellé affiché est traduit. */
  getDayLabel(day: string): string {
    return this.translate.instant('profile-ia.days.' + day);
  }

  ngOnInit() {
    // L'effect se charge du premier appel
  }

  constructor() {
    effect(() => {
      const establishmentId = this.session.selectedId();
      if (establishmentId) {
        this.loadConfig(establishmentId);
      }
    }, { allowSignalWrites: true });
  }

  private loadConfig(establishmentId: string) {
    this.loading.set(true);
    this.wizardConfigService.getEstablishmentConfig(establishmentId).subscribe({
      next: (configs) => {
        this.configs.set(configs);
        const configuredQueue = configs.find(q => q.config);
        if (configuredQueue) {
          this.selectedQueue.set(configuredQueue);
          this.originalConfig = JSON.parse(JSON.stringify(configuredQueue.config));
        } else {
          this.selectedQueue.set(null);
          this.originalConfig = null;
        }
        this.hasChanges.set(false);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Erreur config:', err);
        this.configs.set([]);
        this.selectedQueue.set(null);
        this.originalConfig = null;
        this.loading.set(false);
      }
    });
  }

  updateDemandField(field: string, value: any) {
    const current = this.selectedQueue();
    if (!current?.config) return;
    
    this.selectedQueue.set({
      ...current,
      config: {
        ...current.config,
        data: {
          ...current.config.data,
          demand: {
            ...current.config.data.demand,
            [field]: value
          }
        }
      }
    });
    this.hasChanges.set(true);
  }

  updateServiceField(field: string, value: any) {
    const current = this.selectedQueue();
    if (!current?.config) return;
    
    this.selectedQueue.set({
      ...current,
      config: {
        ...current.config,
        data: {
          ...current.config.data,
          service: {
            ...current.config.data.service,
            [field]: value
          }
        }
      }
    });
    this.hasChanges.set(true);
  }

  updateStaffPerSlotDefault(value: number) {
    const current = this.selectedQueue();
    if (!current?.config?.data?.service?.staff_per_slot) return;
    
    this.updateServiceField('staff_per_slot', {
      ...current.config.data.service.staff_per_slot,
      default: value
    });
  }

  getPeakWindows(): string[][] {
    const windows = this.selectedQueue()?.config?.data?.demand?.peak_windows || [];
    return windows.length > 0 ? windows : [['08:00', '09:00']];
  }

  updatePeakWindow(index: number, timeIndex: number, value: string) {
    const windows = [...this.getPeakWindows()];
    windows[index][timeIndex] = value;
    this.updateDemandField('peak_windows', windows);
  }

  addPeakWindow() {
    const windows = [...this.getPeakWindows(), ['08:00', '09:00']];
    this.updateDemandField('peak_windows', windows);
  }

  removePeakWindow(index: number) {
    const windows = this.getPeakWindows();
    const windowToRemove = windows[index];
    const updatedWindows = windows.filter((_, i) => i !== index);
    
    // Remove corresponding staff entry
    if (windowToRemove) {
      const timeRange = `${windowToRemove[0]}-${windowToRemove[1]}`;
      const current = this.selectedQueue();
      if (current?.config?.data?.service?.staff_per_slot?.peaks) {
        const updatedPeaks = { ...current.config.data.service.staff_per_slot.peaks };
        delete updatedPeaks[timeRange];
        
        this.updateServiceField('staff_per_slot', {
          ...current.config.data.service.staff_per_slot,
          peaks: updatedPeaks
        });
      }
    }
    
    this.updateDemandField('peak_windows', updatedWindows.length > 0 ? updatedWindows : []);
  }

  getPeakStaffForWindow(window: string[]): number {
    const timeRange = `${window[0]}-${window[1]}`;
    const peaks = this.selectedQueue()?.config?.data?.service?.staff_per_slot?.peaks || {};
    return peaks[timeRange] || 1;
  }

  updatePeakStaffForWindow(window: string[], staff: number) {
    const timeRange = `${window[0]}-${window[1]}`;
    const current = this.selectedQueue();
    if (current?.config?.data?.service?.staff_per_slot) {
      const updatedPeaks = {
        ...current.config.data.service.staff_per_slot.peaks,
        [timeRange]: staff
      };
      
      this.updateServiceField('staff_per_slot', {
        ...current.config.data.service.staff_per_slot,
        peaks: updatedPeaks
      });
    }
  }

  getShareAppointmentsPct(): number {
    const share = this.selectedQueue()?.config?.data?.demand?.share_appointments || 0;
    return Math.round(share * 100);
  }

  updateShareAppointmentsPct(pct: number) {
    this.updateDemandField('share_appointments', pct / 100);
  }

  getDifficultCasesPct(): number {
    const share = this.selectedQueue()?.config?.data?.demand?.difficult_cases_share || 0;
    return Math.round(share * 100);
  }

  updateDifficultCasesPct(pct: number) {
    this.updateDemandField('difficult_cases_share', pct / 100);
  }

  openWizard() {
    // Dispatch a custom event that the shell component will listen to
    window.dispatchEvent(new CustomEvent('openWizardModal'));
  }

  isLowDay(day: string): boolean {
    const lowDays = this.selectedQueue()?.config?.data?.demand?.low_days;
    return Array.isArray(lowDays) && lowDays.includes(day);
  }

  isNormalDay(day: string): boolean {
    const normalDays = this.selectedQueue()?.config?.data?.demand?.normal_days;
    return Array.isArray(normalDays) && normalDays.includes(day);
  }

  isHighDay(day: string): boolean {
    const highDays = this.selectedQueue()?.config?.data?.demand?.high_days;
    return Array.isArray(highDays) && highDays.includes(day);
  }

  toggleLowDay(day: string) {
    const current = this.selectedQueue()?.config?.data?.demand?.low_days || [];
    const updated = current.includes(day) 
      ? current.filter(d => d !== day && d.trim() !== '')
      : [...current.filter(d => d.trim() !== ''), day];
    this.updateDemandField('low_days', updated);
    if (!current.includes(day)) {
      this.removeDayFromOtherCategories(day, 'low');
    }
  }

  toggleNormalDay(day: string) {
    const current = this.selectedQueue()?.config?.data?.demand?.normal_days || [];
    const updated = current.includes(day) 
      ? current.filter((d: string) => d !== day && d.trim() !== '')
      : [...current.filter((d: string) => d.trim() !== ''), day];
    this.updateDemandField('normal_days', updated);
    if (!current.includes(day)) {
      this.removeDayFromOtherCategories(day, 'normal');
    }
  }

  toggleHighDay(day: string) {
    const current = this.selectedQueue()?.config?.data?.demand?.high_days || [];
    const updated = current.includes(day) 
      ? current.filter(d => d !== day && d.trim() !== '')
      : [...current.filter(d => d.trim() !== ''), day];
    this.updateDemandField('high_days', updated);
    if (!current.includes(day)) {
      this.removeDayFromOtherCategories(day, 'high');
    }
  }

  private removeDayFromOtherCategories(day: string, except: 'low' | 'normal' | 'high') {
    const demand = this.selectedQueue()?.config?.data?.demand;
    if (!demand) return;

    if (except !== 'low') {
      const lowDays = (demand.low_days || []).filter((d: string) => d !== day);
      this.updateDemandField('low_days', lowDays);
    }
    if (except !== 'normal') {
      const normalDays = (demand.normal_days || []).filter((d: string) => d !== day);
      this.updateDemandField('normal_days', normalDays);
    }
    if (except !== 'high') {
      const highDays = (demand.high_days || []).filter((d: string) => d !== day);
      this.updateDemandField('high_days', highDays);
    }
  }

  saveConfig() {
    if (!this.hasChanges()) {
      this.toastService.show(this.translate.instant('profile-ia.toast.noChangeTitle'), 'info', 3000, this.translate.instant('profile-ia.toast.noChangeBody'));
      return;
    }
    this.showConfirmModal.set(true);
  }

  confirmSave() {
    const current = this.selectedQueue();
    if (!current?.config) return;
    
    this.saving.set(true);
    this.showConfirmModal.set(false);
    this.wizardConfigService.updateQueueConfig(current.queueId, current.config).subscribe({
      next: () => {
        this.saving.set(false);
        this.hasChanges.set(false);
        this.originalConfig = JSON.parse(JSON.stringify(current.config));
        this.toastService.show(this.translate.instant('profile-ia.toast.savedTitle'), 'success', 3000, this.translate.instant('profile-ia.toast.savedBody'));
      },
      error: (err) => {
        console.error('Save error:', err);
        this.saving.set(false);
        this.toastService.show(this.translate.instant('profile-ia.toast.saveErrorTitle'), 'error', 5000, this.translate.instant('profile-ia.toast.saveErrorBody'));
      }
    });
  }

  cancelSave() {
    this.showConfirmModal.set(false);
  }

  cancelChanges() {
    if (this.originalConfig) {
      const current = this.selectedQueue();
      if (current) {
        this.selectedQueue.set({
          ...current,
          config: JSON.parse(JSON.stringify(this.originalConfig))
        });
      }
    }
    this.hasChanges.set(false);
  }
}